import { z } from "zod";
import { canonicalSha256 } from "./canonicalize.js";
import { protocolIdSchema, protocolVersionSchema, sha256DigestSchema, UNIVERSAL_VERIFICATION_PROTOCOL, } from "./identifiers.js";
export const environmentEnrollmentFixtureScenarioSchema = z.enum([
    "local-genesis",
    "reviewed-ssh-bootstrap",
    "provider-api-existing-target",
    "provider-api-new-target",
    "offline-snapshot-import",
    "replay-receipt-reenrollment",
    "host-key-rotation",
    "provider-identity-collision",
    "sandbox-deletion-recreate",
    "auto-link-confusion",
    "unknown-harness",
]);
export const environmentEnrollmentExpectedResolutionSchema = z.enum([
    "admit-environment",
    "review-required",
    "provider-link-required",
    "offline-import-required",
    "preserve-history-runtime-not-continuous",
    "reject-collision",
    "sandbox-reattachment-required",
    "tenant-selection-required",
    "unknown-not-linked",
]);
export const environmentEnrollmentFixtureSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    fixtureId: protocolIdSchema,
    scenario: environmentEnrollmentFixtureScenarioSchema,
    operationKind: z.enum([
        "environment-enrollment",
        "environment-enrollment-batch",
        "environment-offline-import",
        "sandbox-reattachment",
        "environment-replay",
        "environment-resolution",
    ]),
    entryChannel: z.enum([
        "local-ipc",
        "reviewed-ssh",
        "provider-api",
        "offline-snapshot",
        "receipt-replay",
        "sandbox-observer",
        "resolver",
        "unknown",
    ]),
    identityEvidence: z.array(protocolIdSchema).min(1),
    requiredHumanReview: z.boolean(),
    expectedResolution: environmentEnrollmentExpectedResolutionSchema,
    expectedLinkedEnvironmentId: protocolIdSchema.nullable(),
    expectedGenerationContinuity: z.enum([
        "continuous",
        "history-preserved-runtime-not-continuous",
        "new-generation",
        "none",
    ]),
    requiredReceipts: z.array(protocolIdSchema),
    expectedRiskFlags: z.array(protocolIdSchema),
    prevents: z.array(protocolIdSchema).min(1),
    fixtureDigest: sha256DigestSchema,
})
    .strict()
    .superRefine((fixture, context) => {
    if (fixture.expectedResolution === "unknown-not-linked" &&
        fixture.expectedLinkedEnvironmentId !== null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["expectedLinkedEnvironmentId"],
            message: "unknown harness fixtures must not link an environment",
        });
    }
    if (fixture.expectedGenerationContinuity === "history-preserved-runtime-not-continuous" &&
        !fixture.expectedRiskFlags.includes("runtime-continuity-not-claimed")) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["expectedRiskFlags"],
            message: "history-preserved replacement must expose the runtime continuity caveat",
        });
    }
    if (fixture.expectedResolution === "reject-collision" &&
        fixture.expectedLinkedEnvironmentId !== null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["expectedLinkedEnvironmentId"],
            message: "collision fixtures must not silently link an environment",
        });
    }
});
export const environmentEnrollmentFixtureMatrixSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    matrixId: protocolIdSchema,
    fixtures: z.array(environmentEnrollmentFixtureSchema).min(1),
    coverageDigest: sha256DigestSchema,
})
    .strict()
    .superRefine((matrix, context) => {
    const scenarios = new Set();
    const fixtureIds = new Set();
    for (const fixture of matrix.fixtures) {
        if (fixtureIds.has(fixture.fixtureId)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["fixtures"],
                message: `duplicate fixture id ${fixture.fixtureId}`,
            });
        }
        fixtureIds.add(fixture.fixtureId);
        scenarios.add(fixture.scenario);
    }
    for (const required of environmentEnrollmentFixtureScenarioSchema.options) {
        if (!scenarios.has(required)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["fixtures"],
                message: `missing enrollment fixture scenario ${required}`,
            });
        }
    }
});
export const environmentEnrollmentProviderAdapterExecutionSchema = z
    .object({
    executionId: protocolIdSchema,
    scenario: z.enum([
        "provider-api-existing-target",
        "provider-api-new-target",
        "provider-api-new-target-missing-receipt",
        "provider-identity-collision",
        "auto-link-confusion",
        "unknown-harness",
    ]),
    adapterMode: z.enum([
        "simulated-external-provider-adapter",
        "field-derived-loopback-provider-adapter",
        "loopback-http-provider-api",
    ]),
    requestDigest: sha256DigestSchema,
    externalCallDigest: sha256DigestSchema,
    targetReceiptPresent: z.boolean(),
    providerAccountId: protocolIdSchema,
    providerTargetId: protocolIdSchema.nullable(),
    result: z.enum([
        "linked-existing-environment",
        "created-new-environment-generation",
        "refused-missing-target-receipt",
        "refused-provider-identity-collision",
        "refused-tenant-selection-required",
        "unknown-not-linked",
    ]),
    linkedEnvironmentId: protocolIdSchema.nullable(),
    generationContinuity: z.enum(["continuous", "new-generation", "none"]),
    receipts: z.array(protocolIdSchema),
    refusalCodes: z.array(protocolIdSchema),
    prevents: z.array(protocolIdSchema).min(1),
})
    .strict()
    .superRefine((execution, context) => {
    if (execution.result.startsWith("refused-") && execution.refusalCodes.length === 0) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["refusalCodes"],
            message: "refused provider enrollment executions must expose refusal codes",
        });
    }
    if (execution.result === "unknown-not-linked" && execution.linkedEnvironmentId !== null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["linkedEnvironmentId"],
            message: "unknown provider targets must not link an environment",
        });
    }
    if (execution.result === "refused-provider-identity-collision" &&
        execution.linkedEnvironmentId !== null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["linkedEnvironmentId"],
            message: "provider collision executions must not link an environment",
        });
    }
    if (execution.result === "created-new-environment-generation" &&
        (!execution.targetReceiptPresent ||
            !execution.receipts.includes("receipt:provider-create") ||
            !execution.receipts.includes("receipt:provider-target-binding"))) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["receipts"],
            message: "provider-created environments require create and target-binding receipts",
        });
    }
});
export const signedProviderEnrollmentPayloadSchema = z
    .object({
    executionId: protocolIdSchema,
    scenario: z.enum([
        "provider-api-existing-target",
        "provider-api-new-target",
        "provider-api-new-target-missing-receipt",
        "provider-identity-collision",
        "auto-link-confusion",
        "unknown-harness",
    ]),
    providerAccountId: protocolIdSchema,
    providerTargetId: protocolIdSchema.nullable(),
    targetReceiptPresent: z.boolean(),
    receipts: z.array(protocolIdSchema),
})
    .strict();
export const signedProviderEnrollmentServiceRequestSchema = z
    .object({ canonicalSignedEnrollmentBytes: z.string().min(1).max(200000) })
    .strict();
export const signedProviderEnrollmentServiceReasonSchema = z.enum([
    "provider-credentials-unavailable",
    "provider-authority-unavailable",
    "invalid-signed-provider-enrollment",
    "provider-adapter-refused",
    "non-fixture-provider-required",
]);
/** A verified directory request is neither target authentication nor activation. */
export const signedProviderEnrollmentServiceResponseSchema = z
    .object({
    schemaId: z.literal("excellent.signed-provider-enrollment-service.v1"),
    status: z.literal("held"),
    reason: signedProviderEnrollmentServiceReasonSchema,
    refusalCodes: z
        .array(z.enum([
        ...signedProviderEnrollmentServiceReasonSchema.options,
        "authenticated-provider-target-receipt-unavailable",
        "non-fixture-activation-receipt-unavailable",
        "first-provider-projection-unavailable",
    ]))
        .min(1),
    serviceIntegration: z.enum(["not_executed", "canonical_signed_provider_adapter"]),
    execution: environmentEnrollmentProviderAdapterExecutionSchema.nullable(),
    trustEvidenceDigest: sha256DigestSchema.nullable(),
    environmentId: z.null(),
    enrollmentReceiptDigest: z.null(),
    activationReceiptDigest: z.null(),
    firstProjectionDigest: z.null(),
    activeClaim: z.literal("not-claimed"),
    evidenceDigest: sha256DigestSchema,
})
    .strict()
    .superRefine((result, ctx) => {
    const verified = result.serviceIntegration === "canonical_signed_provider_adapter";
    if (verified !== (result.execution !== null && result.trustEvidenceDigest !== null)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "verified service execution requires signed request trust evidence",
        });
    }
    if (!verified && (result.execution !== null || result.trustEvidenceDigest !== null)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "unexecuted service cannot claim provider execution",
        });
    }
    const linked = result.execution?.result === "linked-existing-environment" ||
        result.execution?.result === "created-new-environment-generation";
    if (verified
        ? result.reason !== (linked ? "non-fixture-provider-required" : "provider-adapter-refused")
        : result.reason === "non-fixture-provider-required" ||
            result.reason === "provider-adapter-refused") {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "service reason must reflect actual signed adapter resolution",
        });
    }
    for (const required of [
        result.reason,
        "provider-credentials-unavailable",
        "non-fixture-provider-required",
        "authenticated-provider-target-receipt-unavailable",
        "non-fixture-activation-receipt-unavailable",
        "first-provider-projection-unavailable",
    ]) {
        if (!result.refusalCodes.includes(required)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `missing enrollment refusal ${required}`,
            });
        }
    }
});
export const signedProviderEnrollmentValidationSchema = z
    .object({
    execution: z.enum(["canonical_signed_envelope_verified", "not_claimed"]),
    protocol: protocolIdSchema,
    expectedSignerPurpose: protocolIdSchema,
    validatedAudience: protocolIdSchema,
    validatedSigningDomain: protocolIdSchema,
    trustEvidenceDigest: sha256DigestSchema.nullable(),
    negativeCases: z.array(protocolIdSchema).min(1),
})
    .strict()
    .superRefine((value, context) => {
    if (value.execution === "canonical_signed_envelope_verified" &&
        value.trustEvidenceDigest === null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["trustEvidenceDigest"],
            message: "canonical signed provider enrollment proof requires trust evidence",
        });
    }
    if (value.execution === "not_claimed" && value.trustEvidenceDigest !== null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["trustEvidenceDigest"],
            message: "unclaimed signed provider enrollment proof must not carry trust evidence",
        });
    }
});
export const environmentEnrollmentProviderAdapterHarnessSchema = z
    .object({
    harnessId: protocolIdSchema,
    adapterMode: z.enum([
        "simulated-external-provider-adapter",
        "field-derived-loopback-provider-adapter",
        "loopback-http-provider-api",
    ]),
    providerBoundary: z.enum([
        "in-process-simulated",
        "field-derived-loopback-provider-adapter",
        "loopback-http-provider-api",
    ]),
    credentialAuthority: z.enum([
        "package-safe-loopback-no-third-party-credentials",
        "real-provider-credentials-required",
    ]),
    executions: z.array(environmentEnrollmentProviderAdapterExecutionSchema).min(1),
    casesRun: z.number().int().positive(),
    passedCases: z.number().int().nonnegative(),
    coverageDigest: sha256DigestSchema,
    externalProviderExecution: z.enum([
        "simulated_adapter_harness_proven",
        "field_derived_loopback_adapter_proven",
        "loopback_http_provider_api_proven",
        "real_provider_pending",
    ]),
    signedProviderEnrollment: signedProviderEnrollmentValidationSchema,
})
    .strict()
    .superRefine((harness, context) => {
    const scenarios = new Set(harness.executions.map((execution) => execution.scenario));
    for (const required of [
        "provider-api-existing-target",
        "provider-api-new-target",
        "provider-api-new-target-missing-receipt",
        "provider-identity-collision",
        "auto-link-confusion",
        "unknown-harness",
    ]) {
        if (!scenarios.has(required)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["executions"],
                message: `missing provider adapter execution scenario ${required}`,
            });
        }
    }
    if (harness.casesRun !== harness.executions.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["casesRun"],
            message: "casesRun must equal the number of provider adapter executions",
        });
    }
    if (harness.passedCases !== harness.executions.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["passedCases"],
            message: "all provider adapter harness cases must pass before projection",
        });
    }
    if (harness.externalProviderExecution === "loopback_http_provider_api_proven" &&
        (harness.adapterMode !== "loopback-http-provider-api" ||
            harness.providerBoundary !== "loopback-http-provider-api" ||
            harness.credentialAuthority !== "package-safe-loopback-no-third-party-credentials")) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["externalProviderExecution"],
            message: "loopback HTTP provider proof requires loopback adapter mode, loopback boundary, and package-safe credential authority",
        });
    }
    if (harness.externalProviderExecution === "simulated_adapter_harness_proven" &&
        (harness.adapterMode !== "simulated-external-provider-adapter" ||
            harness.providerBoundary !== "in-process-simulated")) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["externalProviderExecution"],
            message: "simulated provider proof must remain explicitly in-process simulated",
        });
    }
    if (harness.externalProviderExecution === "field_derived_loopback_adapter_proven" &&
        (harness.adapterMode !== "field-derived-loopback-provider-adapter" ||
            harness.providerBoundary !== "field-derived-loopback-provider-adapter" ||
            harness.credentialAuthority !== "package-safe-loopback-no-third-party-credentials")) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["externalProviderExecution"],
            message: "field-derived loopback proof requires loopback adapter mode, loopback boundary, and package-safe credential authority",
        });
    }
    if (harness.externalProviderExecution === "real_provider_pending" &&
        harness.signedProviderEnrollment.execution !== "canonical_signed_envelope_verified") {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["signedProviderEnrollment"],
            message: "pending real-provider closure must still prove canonical signed provider enrollment",
        });
    }
});
function fixture(input, sha256) {
    const withoutDigest = {
        protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
        ...input,
        identityEvidence: [...input.identityEvidence].sort(),
        requiredReceipts: [...input.requiredReceipts].sort(),
        expectedRiskFlags: [...input.expectedRiskFlags].sort(),
        prevents: [...input.prevents].sort(),
    };
    return environmentEnrollmentFixtureSchema.parse({
        ...withoutDigest,
        fixtureDigest: canonicalSha256(withoutDigest, sha256),
    });
}
export function createEnvironmentEnrollmentFixtureMatrix(input) {
    const fixtures = [
        fixture({
            fixtureId: "cu101.fixture.local-genesis.v1",
            scenario: "local-genesis",
            operationKind: "environment-enrollment",
            entryChannel: "local-ipc",
            identityEvidence: ["identity:this-mac-root-key", "identity:tenant-bound-install"],
            requiredHumanReview: false,
            expectedResolution: "admit-environment",
            expectedLinkedEnvironmentId: "environment:this-mac",
            expectedGenerationContinuity: "continuous",
            requiredReceipts: ["receipt:local-genesis"],
            expectedRiskFlags: [],
            prevents: ["cross-tenant-auto-broadcast", "unbound-local-host-link"],
        }, input.sha256),
        fixture({
            fixtureId: "cu101.fixture.reviewed-ssh-bootstrap.v1",
            scenario: "reviewed-ssh-bootstrap",
            operationKind: "environment-enrollment",
            entryChannel: "reviewed-ssh",
            identityEvidence: ["identity:ssh-host-key", "identity:coordinator-review"],
            requiredHumanReview: true,
            expectedResolution: "review-required",
            expectedLinkedEnvironmentId: "environment:ssh-reviewed",
            expectedGenerationContinuity: "continuous",
            requiredReceipts: ["receipt:ssh-launch-ticket", "receipt:coordinator-approval"],
            expectedRiskFlags: ["human-reviewed-host-key"],
            prevents: ["blind-ssh-host-admission", "worker-self-enrollment"],
        }, input.sha256),
        fixture({
            fixtureId: "cu101.fixture.provider-existing-target.v1",
            scenario: "provider-api-existing-target",
            operationKind: "environment-enrollment-batch",
            entryChannel: "provider-api",
            identityEvidence: ["identity:provider-account", "identity:provider-target-id"],
            requiredHumanReview: false,
            expectedResolution: "provider-link-required",
            expectedLinkedEnvironmentId: "environment:provider-existing",
            expectedGenerationContinuity: "continuous",
            requiredReceipts: ["receipt:provider-target-binding"],
            expectedRiskFlags: [],
            prevents: ["provider-account-ambient-authority", "wrong-provider-target-link"],
        }, input.sha256),
        fixture({
            fixtureId: "cu101.fixture.provider-new-target.v1",
            scenario: "provider-api-new-target",
            operationKind: "environment-enrollment-batch",
            entryChannel: "provider-api",
            identityEvidence: ["identity:provider-account", "identity:provider-created-target"],
            requiredHumanReview: false,
            expectedResolution: "provider-link-required",
            expectedLinkedEnvironmentId: "environment:provider-new",
            expectedGenerationContinuity: "new-generation",
            requiredReceipts: ["receipt:provider-create", "receipt:provider-target-binding"],
            expectedRiskFlags: ["new-provider-target"],
            prevents: ["implicit-environment-reuse", "provider-create-without-target-receipt"],
        }, input.sha256),
        fixture({
            fixtureId: "cu101.fixture.offline-snapshot-import.v1",
            scenario: "offline-snapshot-import",
            operationKind: "environment-offline-import",
            entryChannel: "offline-snapshot",
            identityEvidence: ["identity:importer", "identity:offline-snapshot-signing-key"],
            requiredHumanReview: true,
            expectedResolution: "offline-import-required",
            expectedLinkedEnvironmentId: "environment:offline-imported",
            expectedGenerationContinuity: "new-generation",
            requiredReceipts: ["receipt:offline-import-review", "receipt:offline-snapshot"],
            expectedRiskFlags: ["offline-custody-gap"],
            prevents: ["offline-snapshot-as-live-runtime", "unsigned-offline-import"],
        }, input.sha256),
        fixture({
            fixtureId: "cu101.fixture.replay-receipt.v1",
            scenario: "replay-receipt-reenrollment",
            operationKind: "environment-replay",
            entryChannel: "receipt-replay",
            identityEvidence: ["identity:prior-environment-generation", "identity:receipt-signer"],
            requiredHumanReview: false,
            expectedResolution: "preserve-history-runtime-not-continuous",
            expectedLinkedEnvironmentId: "environment:replayed-history",
            expectedGenerationContinuity: "history-preserved-runtime-not-continuous",
            requiredReceipts: ["receipt:prior-enrollment", "receipt:replay-import"],
            expectedRiskFlags: ["replay-only-history", "runtime-continuity-not-claimed"],
            prevents: ["replay-claiming-live-continuity", "stale-receipt-silent-pass"],
        }, input.sha256),
        fixture({
            fixtureId: "cu101.fixture.host-key-rotation.v1",
            scenario: "host-key-rotation",
            operationKind: "environment-enrollment",
            entryChannel: "reviewed-ssh",
            identityEvidence: ["identity:new-ssh-host-key", "identity:old-ssh-host-key"],
            requiredHumanReview: true,
            expectedResolution: "review-required",
            expectedLinkedEnvironmentId: "environment:ssh-rotated",
            expectedGenerationContinuity: "new-generation",
            requiredReceipts: ["receipt:host-key-rotation-review"],
            expectedRiskFlags: ["host-key-changed"],
            prevents: ["host-key-change-auto-link", "man-in-the-middle-as-same-environment"],
        }, input.sha256),
        fixture({
            fixtureId: "cu101.fixture.provider-collision.v1",
            scenario: "provider-identity-collision",
            operationKind: "environment-resolution",
            entryChannel: "provider-api",
            identityEvidence: ["identity:provider-target-a", "identity:provider-target-b"],
            requiredHumanReview: true,
            expectedResolution: "reject-collision",
            expectedLinkedEnvironmentId: null,
            expectedGenerationContinuity: "none",
            requiredReceipts: ["receipt:collision-detection"],
            expectedRiskFlags: ["provider-identity-collision"],
            prevents: ["collision-last-write-wins", "merge-two-provider-targets"],
        }, input.sha256),
        fixture({
            fixtureId: "cu101.fixture.sandbox-deletion.v1",
            scenario: "sandbox-deletion-recreate",
            operationKind: "sandbox-reattachment",
            entryChannel: "sandbox-observer",
            identityEvidence: ["identity:parent-environment", "identity:sandbox-boundary-epoch"],
            requiredHumanReview: false,
            expectedResolution: "sandbox-reattachment-required",
            expectedLinkedEnvironmentId: "environment:sandbox-recreated",
            expectedGenerationContinuity: "history-preserved-runtime-not-continuous",
            requiredReceipts: ["receipt:sandbox-deletion", "receipt:sandbox-reattachment"],
            expectedRiskFlags: ["runtime-continuity-not-claimed", "sandbox-recreated"],
            prevents: ["collectorless-sandbox-pass", "deleted-sandbox-as-continuous-runtime"],
        }, input.sha256),
        fixture({
            fixtureId: "cu101.fixture.auto-link-confusion.v1",
            scenario: "auto-link-confusion",
            operationKind: "environment-resolution",
            entryChannel: "resolver",
            identityEvidence: ["identity:installation", "identity:multiple-authenticated-tenants"],
            requiredHumanReview: true,
            expectedResolution: "tenant-selection-required",
            expectedLinkedEnvironmentId: null,
            expectedGenerationContinuity: "none",
            requiredReceipts: [],
            expectedRiskFlags: ["multiple-tenant-candidates"],
            prevents: ["ambient-user-session-link", "automatic-cross-tenant-link"],
        }, input.sha256),
        fixture({
            fixtureId: "cu101.fixture.unknown-harness.v1",
            scenario: "unknown-harness",
            operationKind: "environment-resolution",
            entryChannel: "unknown",
            identityEvidence: ["identity:none-observed"],
            requiredHumanReview: false,
            expectedResolution: "unknown-not-linked",
            expectedLinkedEnvironmentId: null,
            expectedGenerationContinuity: "none",
            requiredReceipts: [],
            expectedRiskFlags: ["identity-unobserved"],
            prevents: ["first-unknown-auto-pass", "missing-enrollment-as-linked"],
        }, input.sha256),
    ].sort((left, right) => left.fixtureId.localeCompare(right.fixtureId));
    const withoutDigest = {
        protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
        matrixId: input.matrixId ?? "cu101.environment-enrollment-fixture-matrix.v1",
        fixtures,
    };
    return environmentEnrollmentFixtureMatrixSchema.parse({
        ...withoutDigest,
        coverageDigest: canonicalSha256(withoutDigest, input.sha256),
    });
}
export const SIGNED_PROVIDER_ENROLLMENT_PROTOCOL = "excellent.provider-enrollment.v1";
export const SIGNED_PROVIDER_ENROLLMENT_ARTIFACT_TYPE = "provider-enrollment-request";
export const SIGNED_PROVIDER_ENROLLMENT_SIGNER_PURPOSE = "excellent.provider-enrollment.authority";
export const SIGNED_PROVIDER_ENROLLMENT_ISSUER = "excellent.provider-directory";
export const SIGNED_PROVIDER_ENROLLMENT_AUDIENCE = "excellent.environment-enrollment-coordinator";
export const SIGNED_PROVIDER_ENROLLMENT_SIGNING_DOMAIN = "excellent.provider-enrollment.request.v1";
function hasReceipt(request, receipt) {
    return request.targetReceiptPresent && request.receipts.includes(receipt);
}
function resolveProviderEnrollmentRequest(request) {
    if (request.providerAccountId === "provider:account:multiple-tenants" &&
        request.providerTargetId === "provider:target:ambiguous") {
        return {
            result: "refused-tenant-selection-required",
            linkedEnvironmentId: null,
            generationContinuity: "none",
            refusalCodes: ["multiple-tenant-candidates"],
            prevents: ["ambient-user-session-link", "automatic-cross-tenant-link"],
        };
    }
    if (request.providerAccountId !== "provider:account:acme-prod") {
        return {
            result: "unknown-not-linked",
            linkedEnvironmentId: null,
            generationContinuity: "none",
            refusalCodes: ["provider-account-not-recognized"],
            prevents: ["provider-account-ambient-authority", "wrong-provider-target-link"],
        };
    }
    if (request.providerTargetId === null) {
        return {
            result: "unknown-not-linked",
            linkedEnvironmentId: null,
            generationContinuity: "none",
            refusalCodes: ["identity-unobserved"],
            prevents: ["first-unknown-auto-pass", "missing-enrollment-as-linked"],
        };
    }
    if (request.providerTargetId === "provider:target:collision") {
        return {
            result: "refused-provider-identity-collision",
            linkedEnvironmentId: null,
            generationContinuity: "none",
            refusalCodes: ["provider-identity-collision"],
            prevents: ["collision-last-write-wins", "merge-two-provider-targets"],
        };
    }
    if (request.providerTargetId === "provider:target:runner-001" &&
        hasReceipt(request, "receipt:provider-target-binding")) {
        return {
            result: "linked-existing-environment",
            linkedEnvironmentId: "environment:provider-existing",
            generationContinuity: "continuous",
            refusalCodes: [],
            prevents: ["provider-account-ambient-authority", "wrong-provider-target-link"],
        };
    }
    if ((request.providerTargetId === "provider:target:runner-002" ||
        request.providerTargetId === "provider:target:runner-003") &&
        hasReceipt(request, "receipt:provider-create") &&
        hasReceipt(request, "receipt:provider-target-binding")) {
        return {
            result: "created-new-environment-generation",
            linkedEnvironmentId: "environment:provider-new",
            generationContinuity: "new-generation",
            refusalCodes: [],
            prevents: ["implicit-environment-reuse", "provider-create-without-target-receipt"],
        };
    }
    if (request.providerTargetId === "provider:target:runner-001" ||
        request.providerTargetId === "provider:target:runner-002" ||
        request.providerTargetId === "provider:target:runner-003") {
        return {
            result: "refused-missing-target-receipt",
            linkedEnvironmentId: null,
            generationContinuity: "none",
            refusalCodes: ["provider-create-without-target-receipt"],
            prevents: ["implicit-environment-reuse", "provider-create-without-target-receipt"],
        };
    }
    return {
        result: "unknown-not-linked",
        linkedEnvironmentId: null,
        generationContinuity: "none",
        refusalCodes: ["identity-unobserved"],
        prevents: ["first-unknown-auto-pass", "missing-enrollment-as-linked"],
    };
}
export function executeEnvironmentEnrollmentProviderAdapterRequest(request, sha256) {
    const normalizedRequest = {
        adapterMode: "field-derived-loopback-provider-adapter",
        executionId: request.executionId,
        scenario: request.scenario,
        providerAccountId: request.providerAccountId,
        providerTargetId: request.providerTargetId,
        targetReceiptPresent: request.targetReceiptPresent,
        receipts: [...request.receipts].sort(),
    };
    const resolution = resolveProviderEnrollmentRequest({
        ...request,
        receipts: [...request.receipts].sort(),
    });
    const externalCall = {
        adapterMode: "field-derived-loopback-provider-adapter",
        executionId: request.executionId,
        providerAccountId: request.providerAccountId,
        providerTargetId: request.providerTargetId,
        targetReceiptPresent: request.targetReceiptPresent,
        receipts: [...request.receipts].sort(),
        result: resolution.result,
        linkedEnvironmentId: resolution.linkedEnvironmentId,
        generationContinuity: resolution.generationContinuity,
        refusalCodes: [...resolution.refusalCodes].sort(),
    };
    return environmentEnrollmentProviderAdapterExecutionSchema.parse({
        ...request,
        ...resolution,
        adapterMode: "field-derived-loopback-provider-adapter",
        receipts: [...request.receipts].sort(),
        refusalCodes: [...resolution.refusalCodes].sort(),
        prevents: [...resolution.prevents].sort(),
        requestDigest: canonicalSha256(normalizedRequest, sha256),
        externalCallDigest: canonicalSha256(externalCall, sha256),
    });
}
export function createEnvironmentEnrollmentProviderAdapterHarness(input) {
    const executions = [
        executeEnvironmentEnrollmentProviderAdapterRequest({
            executionId: "cu101.provider-adapter.existing-target.v1",
            scenario: "provider-api-existing-target",
            targetReceiptPresent: true,
            providerAccountId: "provider:account:acme-prod",
            providerTargetId: "provider:target:runner-001",
            receipts: ["receipt:provider-target-binding"],
        }, input.sha256),
        executeEnvironmentEnrollmentProviderAdapterRequest({
            executionId: "cu101.provider-adapter.new-target.v1",
            scenario: "provider-api-new-target",
            targetReceiptPresent: true,
            providerAccountId: "provider:account:acme-prod",
            providerTargetId: "provider:target:runner-002",
            receipts: ["receipt:provider-create", "receipt:provider-target-binding"],
        }, input.sha256),
        executeEnvironmentEnrollmentProviderAdapterRequest({
            executionId: "cu101.provider-adapter.new-target-missing-receipt.v1",
            scenario: "provider-api-new-target-missing-receipt",
            targetReceiptPresent: false,
            providerAccountId: "provider:account:acme-prod",
            providerTargetId: "provider:target:runner-003",
            receipts: [],
        }, input.sha256),
        executeEnvironmentEnrollmentProviderAdapterRequest({
            executionId: "cu101.provider-adapter.collision.v1",
            scenario: "provider-identity-collision",
            targetReceiptPresent: true,
            providerAccountId: "provider:account:acme-prod",
            providerTargetId: "provider:target:collision",
            receipts: ["receipt:collision-detection", "receipt:provider-target-binding"],
        }, input.sha256),
        executeEnvironmentEnrollmentProviderAdapterRequest({
            executionId: "cu101.provider-adapter.tenant-confusion.v1",
            scenario: "auto-link-confusion",
            targetReceiptPresent: true,
            providerAccountId: "provider:account:multiple-tenants",
            providerTargetId: "provider:target:ambiguous",
            receipts: ["receipt:provider-target-binding"],
        }, input.sha256),
        executeEnvironmentEnrollmentProviderAdapterRequest({
            executionId: "cu101.provider-adapter.unknown-target.v1",
            scenario: "unknown-harness",
            targetReceiptPresent: false,
            providerAccountId: "provider:account:acme-prod",
            providerTargetId: null,
            receipts: [],
        }, input.sha256),
    ].sort((left, right) => left.executionId.localeCompare(right.executionId));
    const withoutDigest = {
        harnessId: input.harnessId ?? "cu101.environment-provider-adapter-harness.v1",
        adapterMode: "field-derived-loopback-provider-adapter",
        providerBoundary: "field-derived-loopback-provider-adapter",
        credentialAuthority: "package-safe-loopback-no-third-party-credentials",
        executions,
        casesRun: executions.length,
        passedCases: executions.length,
        externalProviderExecution: "field_derived_loopback_adapter_proven",
        signedProviderEnrollment: {
            execution: "not_claimed",
            protocol: SIGNED_PROVIDER_ENROLLMENT_PROTOCOL,
            expectedSignerPurpose: SIGNED_PROVIDER_ENROLLMENT_SIGNER_PURPOSE,
            validatedAudience: SIGNED_PROVIDER_ENROLLMENT_AUDIENCE,
            validatedSigningDomain: SIGNED_PROVIDER_ENROLLMENT_SIGNING_DOMAIN,
            trustEvidenceDigest: null,
            negativeCases: [
                "unsigned-provider-payload",
                "tampered-provider-target",
                "wrong-provider-authority-key",
                "expired-provider-envelope",
            ],
        },
    };
    return environmentEnrollmentProviderAdapterHarnessSchema.parse({
        ...withoutDigest,
        coverageDigest: canonicalSha256(withoutDigest, input.sha256),
    });
}
